import getConfig from 'next/config';
import get from 'lodash/get';

import {getJsonRequest} from '~/src/utilities/helpers/request';
import {getCached, store, hasKey} from '~/src/utilities/helpers/cache';

const {publicRuntimeConfig: config} = getConfig();

const transformSlide = (slide) => {
    const caption = slide.image.caption || slide.caption;
    const captionTitle = slide.image.captionTitle || slide.captionTitle;
    const credit = slide.image.credit || slide.credit;

    return {
        ...slide,
        type: 'image',
        image: {
            ...slide.image,
            captionTitle: captionTitle,
            credit: credit,
            caption: caption
        }
    };
};

const cacheNameSpace = 'slideshow_page';
const fetchPage = async (page) => {
    if (!hasKey(page, cacheNameSpace)) {
        store(page, getJsonRequest(page), cacheNameSpace, 30);
    }
    return getCached(page, cacheNameSpace);
};

const preloadSlides = async (prevSlides, index, nextpage) => {
    const slides = [...prevSlides];
    const length = slides.length;
    const tooSmall = length < 1 + 2 * config.slideshow.preloadSlides;

    const lowerBound = tooSmall ? 0 : (length + index - config.slideshow.preloadSlides) % length;
    const upperBound = tooSmall ? length - 1 : (length + index + config.slideshow.preloadSlides) % length;

    for (let pos = 0; pos < length; pos++) {
        const outOfRange = lowerBound < upperBound ? pos < lowerBound || pos > upperBound : pos < lowerBound && pos > upperBound;
        if (outOfRange) {
            if (slides[pos].type !== 'slidePlaceholder') {
                slides[pos] = {
                    type: 'slidePlaceholder',
                    data: slides[pos]
                };
            }
        } else {
            while (nextpage && pos <= index && slides[pos].type === 'slidePlaceholder' && !slides[pos].data) {
                const indexPlaceholder = slides.findIndex((item) => item.type === 'slidePlaceholder' && !item.data);
                const data = await fetchPage(nextpage);

                slides.splice(
                    indexPlaceholder,
                    data.content.slides.length,
                    ...data.content.slides.map((slide) => ({
                        type: 'slidePlaceholder',
                        data: transformSlide(slide)
                    }))
                );
                nextpage = data.content.nextpage;
            }

            if (slides[pos].type === 'slidePlaceholder' && slides[pos].data) {
                slides[pos] = slides[pos].data;
            }
        }
    }

    return {slides, nextpage};
};

const fillSlides = (slides, size) => {
    const allSlides = [];

    const minSlideCount = Math.min(slides.length, config.slideshow.preloadSlides + 1);
    for (let i = 0; i < minSlideCount; i++) {
        allSlides.push(transformSlide(slides[i]));
    }

    for (let i = allSlides.length; i < size; i++) {
        allSlides.push({
            type: 'slidePlaceholder',
            data: slides[i] && transformSlide(slides[i])
        });
    }

    return allSlides;
};

const normalizeSlides$ = ({provider = 'unity', slideshow = {}, slides = []}) => {
    switch (provider.toLowerCase()) {
        case 'contentstation':
            return slides.filter((slide = {}) => slide.type === 'image' && slide.image);
        default:
            return slideshow.slides?.filter((slide = {}) => slide.image) || [];
    }
};

const normalizeSlideshow = ({provider = 'unity', slideshow}, meta, slides) => {
    switch (provider.toLowerCase()) {
        case 'contentstation':
            return {
                title: get(meta, 'teaser["default"].title'),
                slideCount: slides.length
            };
        default:
            return slideshow.slideshow;
    }
};

const normalizeNextPage = ({provider = 'unity', slideshow}) => {
    if (provider.toLowerCase() === 'unity') {
        return slideshow.nextpage;
    }
};

const transformSlideshow = (element, meta) => {
    if (element.type === 'slideshow') {
        const slides = normalizeSlides$(element).map(transformSlide);
        const slideshow = normalizeSlideshow(element, meta, slides);
        const nextpage = normalizeNextPage(element);

        return {
            type: 'slideshow',
            slideshow: {
                nextpage,
                slideshow,
                slides
            }
        };
    }

    return element;
};

const slideshowReducer = (state, action) => {
    switch (action.type) {
        case 'loadSlides':
            return {
                ...state,
                nextpage: action.nextpage,
                slides: action.slides
            };
        case 'slide':
            return {
                ...state,
                direction: action.direction,
                index: action.index
            };
        case 'clickSlide':
            if (state.isFullscreen) {
                return {
                    ...state,
                    hideControls: !state.hideControls
                };
            }

            return {
                ...state,
                isFullscreen: true
            };
        case 'closeFullscreen':
            if (state.isFullscreen) {
                return {
                    ...state,
                    hideControls: false,
                    isFullscreen: false
                };
            }

            return state;
        default:
            return state;
    }
};

export {transformSlide, transformSlideshow, fillSlides, preloadSlides, slideshowReducer};
